import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Package, FileUp, Calculator, Download, Users, Mail, LogOut as LogOutIcon, 
  DollarSign, Clock, CheckCircle, XCircle, RefreshCw, Settings, Eye, Menu, X,
  LayoutDashboard, FileText, Send, Edit, Trash2, UserPlus, UserMinus, Key,
  Activity, Filter, Search, Scale, Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout, token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [drops, setDrops] = useState([]);
  const [users, setUsers] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [contentBlocks, setContentBlocks] = useState([]);
  const [legalDocuments, setLegalDocuments] = useState([]);
  const [userOrders, setUserOrders] = useState([]);
  const [allDrops, setAllDrops] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({});
  const [selectedDrop, setSelectedDrop] = useState(null);
  const [showSubmissionsDialog, setShowSubmissionsDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Filter states
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [userAdminFilter, setUserAdminFilter] = useState('all');
  const [submissionTypeFilter, setSubmissionTypeFilter] = useState('all');
  const [emailStatusFilter, setEmailStatusFilter] = useState('all');
  const [orderDropFilter, setOrderDropFilter] = useState('all');
  
  // Dialog states
  const [showTestEmailDialog, setShowTestEmailDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [dropToPublish, setDropToPublish] = useState(null);
  const [showEditDropDialog, setShowEditDropDialog] = useState(false);
  const [editDropForm, setEditDropForm] = useState({});
  const [showEditContentDialog, setShowEditContentDialog] = useState(false);
  const [editContentForm, setEditContentForm] = useState({});
  const [selectedContent, setSelectedContent] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditLegalDialog, setShowEditLegalDialog] = useState(false);
  const [editLegalForm, setEditLegalForm] = useState({ title: '', content: '' });
  const [selectedLegal, setSelectedLegal] = useState(null);
  const [showUserOrderDialog, setShowUserOrderDialog] = useState(false);
  const [selectedUserOrder, setSelectedUserOrder] = useState(null);
  const [userOrderForm, setUserOrderForm] = useState({
    user_email: '',
    drop_id: '',
    items: [],
    item_total: 0,
    shipping_fee: 0,
    service_fee: 0,
    notes: ''
  });
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [withdrawEmail, setWithdrawEmail] = useState('');
  const [withdrawDropId, setWithdrawDropId] = useState('');

  const [newDropForm, setNewDropForm] = useState({
    name: '',
    brand: '',
    description: '',
    start_date: '',
    end_date: '',
    service_fee_standard: 8.0,
    service_fee_oversize: 12.0,
    deposit_percent: 50.0,
    min_participants: 3,
    location: 'Singapore',
    collection_info: ''
  });

  const [invoiceForm, setInvoiceForm] = useState({
    shipping_standard: 0,
    shipping_oversize: 0,
    duties_taxes: 0,
    notes: ''
  });

  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats', error);
    }
  }, [token]);

  const fetchActivities = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/activity?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivities(response.data);
    } catch (error) {
      console.error('Failed to fetch activities', error);
    }
  }, [token]);

  const fetchDrops = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/drops`);
      setDrops(response.data);
    } catch (error) {
      console.error('Failed to fetch drops', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      let url = `${API}/admin/users/search?`;
      if (userSearch) url += `q=${encodeURIComponent(userSearch)}&`;
      if (userStatusFilter !== 'all') url += `status=${userStatusFilter}&`;
      if (userAdminFilter !== 'all') url += `is_admin=${userAdminFilter === 'admin'}&`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  }, [token, userSearch, userStatusFilter, userAdminFilter]);

  const fetchEmailLogs = useCallback(async () => {
    try {
      let url = `${API}/admin/email-logs/filter?limit=100`;
      if (emailStatusFilter !== 'all') url += `&status=${emailStatusFilter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch email logs', error);
    }
  }, [token, emailStatusFilter]);

  const fetchPendingPayments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/payments/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingPayments(response.data);
    } catch (error) {
      console.error('Failed to fetch pending payments', error);
    }
  }, [token]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs', error);
    }
  }, [token]);

  const fetchSubmissions = useCallback(async () => {
    try {
      let url = `${API}/admin/submissions?`;
      if (submissionTypeFilter !== 'all') url += `type=${submissionTypeFilter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch submissions', error);
    }
  }, [token, submissionTypeFilter]);

  const fetchContentBlocks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContentBlocks(response.data);
    } catch (error) {
      console.error('Failed to fetch content blocks', error);
    }
  }, [token]);

  const fetchLegalDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/legal`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLegalDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch legal documents', error);
    }
  }, [token]);

  const fetchUserOrders = useCallback(async () => {
    try {
      let url = `${API}/admin/user-orders?`;
      if (orderDropFilter !== 'all') url += `drop_id=${orderDropFilter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch user orders', error);
    }
  }, [token, orderDropFilter]);

  const fetchAllDrops = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/drops/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllDrops(response.data);
    } catch (error) {
      console.error('Failed to fetch all drops', error);
    }
  }, [token]);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchDashboardStats(),
          fetchActivities(),
          fetchDrops(),
          fetchUsers(),
          fetchEmailLogs(),
          fetchPendingPayments(),
          fetchAuditLogs(),
          fetchSubmissions(),
          fetchContentBlocks(),
          fetchLegalDocuments(),
          fetchUserOrders(),
          fetchAllDrops()
        ]);
      } catch (error) {
        console.error('Failed to fetch data', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [fetchDashboardStats, fetchActivities, fetchDrops, fetchUsers, fetchEmailLogs, fetchPendingPayments, fetchAuditLogs, fetchSubmissions, fetchContentBlocks, fetchLegalDocuments, fetchUserOrders, fetchAllDrops]);

  // Re-fetch users when filters change
  useEffect(() => {
    if (!loading) fetchUsers();
  }, [userSearch, userStatusFilter, userAdminFilter]);

  // Re-fetch submissions when filter changes
  useEffect(() => {
    if (!loading) fetchSubmissions();
  }, [submissionTypeFilter]);

  // Re-fetch email logs when filter changes
  useEffect(() => {
    if (!loading) fetchEmailLogs();
  }, [emailStatusFilter]);

  // Re-fetch user orders when filter changes
  useEffect(() => {
    if (!loading) fetchUserOrders();
  }, [orderDropFilter]);

  const handleCreateDrop = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/drops`, newDropForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Drop created successfully');
      fetchDrops();
      fetchAllDrops();
      fetchDashboardStats();
      setNewDropForm({
        name: '',
        brand: '',
        description: '',
        start_date: '',
        end_date: '',
        service_fee_standard: 8.0,
        service_fee_oversize: 12.0,
        deposit_percent: 50.0,
        min_participants: 3,
        location: 'Singapore',
        collection_info: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create drop');
    }
  };

  const handleUpdateDropStatus = async (dropId, status) => {
    try {
      await axios.patch(`${API}/drops/${dropId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Drop status updated');
      fetchDrops();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleEditDrop = async (e) => {
    e.preventDefault();
    if (!selectedDrop) return;

    try {
      await axios.patch(`${API}/admin/drops/${selectedDrop.id}`, editDropForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Drop updated successfully');
      setShowEditDropDialog(false);
      fetchDrops();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update drop');
    }
  };

  const handleUploadInvoice = async (e) => {
    e.preventDefault();
    if (!selectedDrop) return;

    try {
      await axios.post(`${API}/admin/invoices`, {
        drop_id: selectedDrop.id,
        ...invoiceForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice uploaded successfully');
      setInvoiceForm({
        shipping_standard: 0,
        shipping_oversize: 0,
        duties_taxes: 0,
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload invoice');
    }
  };

  const handleRunAllocation = async (dropId) => {
    try {
      const response = await axios.post(`${API}/admin/allocations/${dropId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Allocation completed for ${response.data.allocations.length} participants`);
      fetchDrops();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run allocation');
    }
  };

  const handleExportDrop = async (dropId) => {
    try {
      const response = await axios.get(`${API}/admin/export/drop/${dropId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `drop_${dropId}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handlePublishDrop = async (dropId, testEmailAddress = null) => {
    try {
      const payload = { drop_id: dropId };
      if (testEmailAddress) {
        payload.test_email = testEmailAddress;
      }
      
      const response = await axios.post(`${API}/admin/drops/publish`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (testEmailAddress) {
        toast.success(`Test email sent to ${testEmailAddress}`);
        setShowTestEmailDialog(false);
        setTestEmail('');
      } else {
        toast.success(`Drop published! Notifications sent to ${response.data.notification_result?.sent || 0} users.`);
      }
      
      fetchDrops();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish drop');
    }
  };

  const handleVerifyPayment = async (paymentId) => {
    try {
      await axios.post(`${API}/admin/payments/${paymentId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment verified');
      fetchPendingPayments();
      fetchDashboardStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify payment');
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated successfully');
      fetchUsers();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleTriggerPasswordReset = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password reset email sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    }
  };

  const handleWithdrawUser = async () => {
    if (!withdrawEmail || !withdrawDropId) {
      toast.error('Please select a drop and enter user email');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/drops/${withdrawDropId}/withdraw-user?user_email=${encodeURIComponent(withdrawEmail)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User withdrawn from drop successfully');
      setShowWithdrawDialog(false);
      setWithdrawEmail('');
      setWithdrawDropId('');
      fetchDrops();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw user');
    }
  };

  const handleCreateUserOrder = async (e) => {
    e.preventDefault();
    if (!userOrderForm.user_email || !userOrderForm.drop_id) {
      toast.error('Please select a user and drop');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/user-orders`, userOrderForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User order saved successfully');
      setShowUserOrderDialog(false);
      setUserOrderForm({
        user_email: '',
        drop_id: '',
        items: [],
        item_total: 0,
        shipping_fee: 0,
        service_fee: 0,
        notes: ''
      });
      fetchUserOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user order');
    }
  };

  const handleDeleteUserOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    
    try {
      await axios.delete(`${API}/admin/user-orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order deleted');
      fetchUserOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  const openEditUserOrderDialog = (order = null) => {
    if (order) {
      setSelectedUserOrder(order);
      setUserOrderForm({
        user_email: order.user_email,
        drop_id: order.drop_id,
        items: order.items || [],
        item_total: order.item_total || 0,
        shipping_fee: order.shipping_fee || 0,
        service_fee: order.service_fee || 0,
        notes: order.notes || ''
      });
    } else {
      setSelectedUserOrder(null);
      setUserOrderForm({
        user_email: '',
        drop_id: '',
        items: [],
        item_total: 0,
        shipping_fee: 0,
        service_fee: 0,
        notes: ''
      });
    }
    setShowUserOrderDialog(true);
  };

  const addItemToOrder = () => {
    setUserOrderForm(prev => ({
      ...prev,
      items: [...prev.items, { name: '', category: 'mallet', quantity: 1, unit_price: 0, notes: '' }]
    }));
  };

  const removeItemFromOrder = (index) => {
    setUserOrderForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateOrderItem = (index, field, value) => {
    setUserOrderForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const handleUpdateLegalDocument = async (e, publish = false) => {
    e.preventDefault();
    if (!selectedLegal) return;

    try {
      await axios.patch(`${API}/admin/legal/${selectedLegal.id}`, {
        ...editLegalForm,
        publish
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(publish ? 'Legal document published! Users will be asked to re-accept.' : 'Legal document updated');
      setShowEditLegalDialog(false);
      fetchLegalDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update legal document');
    }
  };

  const openEditLegalDialog = (doc) => {
    setSelectedLegal(doc);
    setEditLegalForm({
      title: doc.title,
      content: doc.content
    });
    setShowEditLegalDialog(true);
  };

  const handleMarkSubmissionHandled = async (submissionId, handled) => {
    try {
      await axios.patch(`${API}/admin/submissions/${submissionId}/handled`, { handled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Submission status updated');
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update submission');
    }
  };

  const handleExportSubmissions = async () => {
    try {
      const type = submissionTypeFilter !== 'all' ? submissionTypeFilter : '';
      const response = await axios.get(`${API}/admin/submissions/export?type=${type}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `submissions_${type || 'all'}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export submissions');
    }
  };

  const handleResendEmail = async (logId) => {
    try {
      await axios.post(`${API}/admin/email-logs/${logId}/resend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email resent');
      fetchEmailLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    }
  };

  const handleUpdateContent = async (e) => {
    e.preventDefault();
    if (!selectedContent) return;

    try {
      await axios.patch(`${API}/admin/content/${selectedContent.id}`, editContentForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content updated successfully');
      setShowEditContentDialog(false);
      fetchContentBlocks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update content');
    }
  };

  const fetchDropParticipants = async (dropId) => {
    try {
      const response = await axios.get(`${API}/admin/drop-participants/${dropId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch participants', error);
      return [];
    }
  };

  const openEditDropDialog = (drop) => {
    setSelectedDrop(drop);
    setEditDropForm({
      name: drop.name,
      brand: drop.brand,
      description: drop.description || '',
      min_participants: drop.min_participants || 3,
      service_fee_standard: drop.service_fee_standard,
      service_fee_oversize: drop.service_fee_oversize,
      deposit_percent: drop.deposit_percent || 50,
      location: drop.location || 'Singapore',
      collection_info: drop.collection_info || ''
    });
    setShowEditDropDialog(true);
  };

  const openEditContentDialog = (content) => {
    setSelectedContent(content);
    setEditContentForm({
      title: content.title,
      body: content.body
    });
    setShowEditContentDialog(true);
  };

  const openUserDialog = (userItem) => {
    setSelectedUser(userItem);
    setShowUserDialog(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-green-100 text-green-800',
      confirming: 'bg-yellow-100 text-yellow-800',
      ordered: 'bg-blue-100 text-blue-800',
      in_transit: 'bg-purple-100 text-purple-800',
      ready_for_collection: 'bg-orange-100 text-orange-800',
      completed: 'bg-gray-100 text-gray-800',
      not_proceeding: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getActivityIcon = (type) => {
    const icons = {
      user_signup: <UserPlus className="w-4 h-4 text-green-500" />,
      join_drop: <Package className="w-4 h-4 text-blue-500" />,
      request_drop: <FileText className="w-4 h-4 text-purple-500" />,
      drop_status_change: <RefreshCw className="w-4 h-4 text-orange-500" />,
      withdrawal: <UserMinus className="w-4 h-4 text-red-500" />,
    };
    return icons[type] || <Activity className="w-4 h-4 text-gray-500" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9FB]">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9FB]" data-testid="admin-dashboard-page">
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 bg-primary text-white">
        <span className="text-lg font-playfair font-bold">Admin</span>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2" data-testid="mobile-menu-toggle">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50
          w-64 min-h-screen bg-primary text-white p-6 flex flex-col
          transition-transform duration-300 ease-in-out
        `}>
          <Link to="/" className="text-xl font-playfair font-bold block mb-8 hidden lg:block">
            Sharedequal Admin
          </Link>
          <nav className="space-y-1 flex-1 mt-4 lg:mt-0">
            {[
              { id: 'overview', label: 'Overview', icon: LayoutDashboard },
              { id: 'drops', label: 'Drops', icon: Package },
              { id: 'users', label: 'Users', icon: Users },
              { id: 'orders', label: 'User Orders', icon: DollarSign },
              { id: 'submissions', label: 'Submissions', icon: FileText },
              { id: 'payments', label: 'Payments', icon: CheckCircle },
              { id: 'emails', label: 'Email Logs', icon: Mail },
              { id: 'content', label: 'Content', icon: Edit },
              { id: 'legal', label: 'Legal', icon: Scale },
              { id: 'invoices', label: 'Invoices', icon: FileUp },
              { id: 'allocations', label: 'Allocations', icon: Calculator },
              { id: 'audit', label: 'Audit Logs', icon: Clock },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => { setActiveTab(id); setSidebarOpen(false); }}
                className={`w-full text-left px-4 py-2.5 rounded-lg flex items-center gap-3 transition-colors ${
                  activeTab === id ? 'bg-white/20' : 'hover:bg-white/10'
                }`}
                data-testid={`admin-nav-${id}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </nav>
          <div className="space-y-2 pt-4 border-t border-white/20">
            <Link to="/dashboard" className="block" onClick={() => setSidebarOpen(false)}>
              <Button variant="secondary" className="w-full rounded-lg text-sm">
                Player Dashboard
              </Button>
            </Link>
            <button
              onClick={logout}
              className="w-full text-left px-4 py-2.5 rounded-lg flex items-center gap-3 hover:bg-white/10 transition-colors text-white/80"
            >
              <LogOutIcon className="w-4 h-4" />
              Logout
            </button>
          </div>
        </aside>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl lg:text-3xl font-playfair font-bold text-gray-900" data-testid="admin-heading">
                  Dashboard Overview
                </h1>
                <p className="text-muted-foreground mt-1">Welcome back, {user?.full_name || 'Admin'}</p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                <Card className="border-stone-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Total Users</p>
                        <p className="text-2xl font-bold">{dashboardStats.total_users || 0}</p>
                      </div>
                      <Users className="w-8 h-8 text-primary/50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-stone-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Total Drops</p>
                        <p className="text-2xl font-bold">{dashboardStats.total_drops || 0}</p>
                      </div>
                      <Package className="w-8 h-8 text-primary/50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-stone-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Join Submissions</p>
                        <p className="text-2xl font-bold">{dashboardStats.join_submissions || 0}</p>
                      </div>
                      <FileText className="w-8 h-8 text-blue-500/50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-stone-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Request Submissions</p>
                        <p className="text-2xl font-bold">{dashboardStats.request_submissions || 0}</p>
                      </div>
                      <FileText className="w-8 h-8 text-purple-500/50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-stone-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Total Participants</p>
                        <p className="text-2xl font-bold">{dashboardStats.total_participants || 0}</p>
                      </div>
                      <UserPlus className="w-8 h-8 text-green-500/50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-stone-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Active Drops</p>
                        <p className="text-2xl font-bold">{dashboardStats.active_drops || 0}</p>
                      </div>
                      <Activity className="w-8 h-8 text-orange-500/50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-stone-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Pending Payments</p>
                        <p className="text-2xl font-bold">{dashboardStats.pending_payments || 0}</p>
                      </div>
                      <DollarSign className="w-8 h-8 text-orange-500/50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-stone-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Emails Sent</p>
                        <p className="text-2xl font-bold">{emailLogs.filter(e => e.status === 'success').length}</p>
                      </div>
                      <Mail className="w-8 h-8 text-green-500/50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity Feed */}
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Recent Activity
                  </CardTitle>
                  <CardDescription>Latest 20 events across the platform</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {activities.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">No recent activity</p>
                    ) : (
                      activities.map((activity) => (
                        <div key={activity.id} className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg">
                          {getActivityIcon(activity.type)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">
                              {activity.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {activity.user_email || activity.details?.full_name || 'System'}
                              {activity.details?.drop_name && ` - ${activity.details.drop_name}`}
                            </p>
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(activity.timestamp).toLocaleString()}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h2 className="text-2xl font-semibold">User Management</h2>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowWithdrawDialog(true)} className="gap-2">
                    <UserMinus className="w-4 h-4" /> Withdraw User
                  </Button>
                  <Button variant="outline" onClick={fetchUsers} className="gap-2">
                    <RefreshCw className="w-4 h-4" /> Refresh
                  </Button>
                </div>
              </div>

              {/* Filters */}
              <Card className="border-stone-200">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by name or email..."
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                          className="pl-10"
                          data-testid="user-search-input"
                        />
                      </div>
                    </div>
                    <Select value={userStatusFilter} onValueChange={setUserStatusFilter}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={userAdminFilter} onValueChange={setUserAdminFilter}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="player">Player</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Users Table */}
              <Card className="border-stone-200">
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="text-left p-4 font-medium text-sm">Name</th>
                        <th className="text-left p-4 font-medium text-sm">Email</th>
                        <th className="text-left p-4 font-medium text-sm">Phone</th>
                        <th className="text-left p-4 font-medium text-sm">Role</th>
                        <th className="text-left p-4 font-medium text-sm">Status</th>
                        <th className="text-left p-4 font-medium text-sm">Drops</th>
                        <th className="text-left p-4 font-medium text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id || u.email} className="border-b border-stone-100 last:border-0">
                          <td className="p-4">{u.full_name || '-'}</td>
                          <td className="p-4 text-sm">{u.email}</td>
                          <td className="p-4 text-sm">{u.phone || '-'}</td>
                          <td className="p-4">
                            <Badge className={u.is_admin ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}>
                              {u.is_admin ? 'Admin' : 'Player'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <Badge className={u.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {u.status || 'active'}
                            </Badge>
                          </td>
                          <td className="p-4 text-sm">{u.drops_joined || 0}</td>
                          <td className="p-4">
                            <Button size="sm" variant="outline" onClick={() => openUserDialog(u)}>
                              <Settings className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Drops Tab */}
          {activeTab === 'drops' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h2 className="text-2xl font-semibold">Drop Management</h2>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={fetchDrops} className="gap-2">
                    <RefreshCw className="w-4 h-4" /> Refresh
                  </Button>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="bg-primary hover:bg-primary/90" data-testid="create-drop-button">
                        Create New Drop
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Create New Drop</DialogTitle>
                        <DialogDescription>Fill in the details for the new group drop</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateDrop} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="name">Drop Name</Label>
                            <Input id="name" value={newDropForm.name} onChange={(e) => setNewDropForm({ ...newDropForm, name: e.target.value })} required />
                          </div>
                          <div>
                            <Label htmlFor="brand">Brand</Label>
                            <Input id="brand" value={newDropForm.brand} onChange={(e) => setNewDropForm({ ...newDropForm, brand: e.target.value })} required />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="description">Description</Label>
                          <Input id="description" value={newDropForm.description} onChange={(e) => setNewDropForm({ ...newDropForm, description: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="start_date">Start Date</Label>
                            <Input id="start_date" type="date" value={newDropForm.start_date} onChange={(e) => setNewDropForm({ ...newDropForm, start_date: e.target.value })} required />
                          </div>
                          <div>
                            <Label htmlFor="end_date">End Date</Label>
                            <Input id="end_date" type="date" value={newDropForm.end_date} onChange={(e) => setNewDropForm({ ...newDropForm, end_date: e.target.value })} required />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label htmlFor="min_participants">Min Participants</Label>
                            <Input id="min_participants" type="number" value={newDropForm.min_participants} onChange={(e) => setNewDropForm({ ...newDropForm, min_participants: Number(e.target.value) })} min="1" required />
                          </div>
                          <div>
                            <Label htmlFor="service_fee_standard">Service Fee (Standard) SGD</Label>
                            <Input id="service_fee_standard" type="number" step="0.01" value={newDropForm.service_fee_standard} onChange={(e) => setNewDropForm({ ...newDropForm, service_fee_standard: Number(e.target.value) })} required />
                          </div>
                          <div>
                            <Label htmlFor="service_fee_oversize">Service Fee (Oversize) SGD</Label>
                            <Input id="service_fee_oversize" type="number" step="0.01" value={newDropForm.service_fee_oversize} onChange={(e) => setNewDropForm({ ...newDropForm, service_fee_oversize: Number(e.target.value) })} required />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="location">Location</Label>
                            <Input id="location" value={newDropForm.location} onChange={(e) => setNewDropForm({ ...newDropForm, location: e.target.value })} />
                          </div>
                          <div>
                            <Label htmlFor="collection_info">Collection Info</Label>
                            <Input id="collection_info" value={newDropForm.collection_info} onChange={(e) => setNewDropForm({ ...newDropForm, collection_info: e.target.value })} placeholder="e.g., Central location TBC" />
                          </div>
                        </div>
                        <Button type="submit" className="w-full bg-primary hover:bg-primary/90">Create Drop</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              <div className="space-y-4">
                {drops.map(drop => (
                  <Card key={drop.id} className="border-stone-200" data-testid={`admin-drop-card-${drop.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">{drop.name}</CardTitle>
                          <CardDescription>{drop.brand} • {drop.participant_count} participants • Min: {drop.min_participants || 3}</CardDescription>
                        </div>
                        <Badge className={getStatusColor(drop.status)}>{drop.status.replace('_', ' ')}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-2 flex-wrap">
                        <Select value={drop.status} onValueChange={(value) => handleUpdateDropStatus(drop.id, value)}>
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="confirming">Confirming</SelectItem>
                            <SelectItem value="ordered">Ordered</SelectItem>
                            <SelectItem value="in_transit">In Transit</SelectItem>
                            <SelectItem value="ready_for_collection">Ready for Collection</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="not_proceeding">Not Proceeding</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" onClick={() => openEditDropDialog(drop)}>
                          <Settings className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        {drop.status === 'open' && !drop.notification_sent_at && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => { setDropToPublish(drop); setShowTestEmailDialog(true); }}>
                              Test Email
                            </Button>
                            <Button size="sm" onClick={() => handlePublishDrop(drop.id)}>
                              Publish & Notify
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="outline" onClick={async () => {
                          const participants = await fetchDropParticipants(drop.id);
                          setSelectedDrop(drop);
                          setSubmissions(participants);
                          setShowSubmissionsDialog(true);
                        }}>
                          <Eye className="w-4 h-4 mr-1" /> View Participants
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleExportDrop(drop.id)}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* User Orders Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h2 className="text-2xl font-semibold">User Orders</h2>
                <div className="flex gap-2">
                  <Button onClick={() => openEditUserOrderDialog()} className="bg-primary hover:bg-primary/90 gap-2">
                    <UserPlus className="w-4 h-4" /> Add Order
                  </Button>
                  <Button variant="outline" onClick={fetchUserOrders} className="gap-2">
                    <RefreshCw className="w-4 h-4" /> Refresh
                  </Button>
                </div>
              </div>

              {/* Filter by Drop */}
              <Card className="border-stone-200">
                <CardContent className="p-4">
                  <div className="flex gap-4 items-center">
                    <Label>Filter by Drop:</Label>
                    <Select value={orderDropFilter} onValueChange={setOrderDropFilter}>
                      <SelectTrigger className="w-[300px]">
                        <SelectValue placeholder="All Drops" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Drops</SelectItem>
                        {allDrops.map(drop => (
                          <SelectItem key={drop.id} value={drop.id}>{drop.name} ({drop.brand})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Orders List */}
              {userOrders.length === 0 ? (
                <Card className="border-stone-200">
                  <CardContent className="p-12 text-center">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-lg text-muted-foreground">No user orders found</p>
                    <p className="text-sm text-muted-foreground mt-2">Add orders for users who have purchased items in a drop</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {userOrders.map((order) => (
                    <Card key={order.id} className="border-stone-200">
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{order.user_email}</CardTitle>
                            <CardDescription>{order.drop_name} ({order.drop_brand})</CardDescription>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openEditUserOrderDialog(order)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteUserOrder(order.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-medium text-sm text-muted-foreground mb-2">Items</h4>
                            {order.items && order.items.length > 0 ? (
                              <ul className="text-sm space-y-1">
                                {order.items.map((item, i) => (
                                  <li key={i} className="flex justify-between">
                                    <span>{item.name} ({item.category}) x{item.quantity}</span>
                                    <span>SGD {(item.unit_price * item.quantity).toFixed(2)}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm text-muted-foreground">No items added</p>
                            )}
                          </div>
                          <div className="bg-stone-50 p-4 rounded-lg">
                            <h4 className="font-medium text-sm text-muted-foreground mb-2">Cost Breakdown</h4>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span>Item Total:</span>
                                <span>SGD {order.item_total?.toFixed(2) || '0.00'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Shipping Fee:</span>
                                <span>SGD {order.shipping_fee?.toFixed(2) || '0.00'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Service Fee:</span>
                                <span>SGD {order.service_fee?.toFixed(2) || '0.00'}</span>
                              </div>
                              <div className="flex justify-between font-bold pt-2 border-t">
                                <span>Grand Total:</span>
                                <span>SGD {order.grand_total?.toFixed(2) || '0.00'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        {order.notes && (
                          <p className="text-sm text-muted-foreground mt-3">Notes: {order.notes}</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Submissions Tab */}
          {activeTab === 'submissions' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h2 className="text-2xl font-semibold">Submissions</h2>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleExportSubmissions} className="gap-2">
                    <Download className="w-4 h-4" /> Export CSV
                  </Button>
                </div>
              </div>

              {/* Filter Tabs */}
              <Tabs value={submissionTypeFilter} onValueChange={setSubmissionTypeFilter}>
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="join_drop">Join Drop</TabsTrigger>
                  <TabsTrigger value="request_drop">Request Drop</TabsTrigger>
                </TabsList>
              </Tabs>

              <Card className="border-stone-200">
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full min-w-[900px]">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="text-left p-4 font-medium text-sm">Type</th>
                        <th className="text-left p-4 font-medium text-sm">User</th>
                        <th className="text-left p-4 font-medium text-sm">Email</th>
                        <th className="text-left p-4 font-medium text-sm">Details</th>
                        <th className="text-left p-4 font-medium text-sm">Notes</th>
                        <th className="text-left p-4 font-medium text-sm">Date</th>
                        <th className="text-left p-4 font-medium text-sm">Handled</th>
                        <th className="text-left p-4 font-medium text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map((s) => (
                        <tr key={s.id} className="border-b border-stone-100 last:border-0">
                          <td className="p-4">
                            <Badge className={s.type === 'join_drop' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                              {s.type === 'join_drop' ? 'Join' : 'Request'}
                            </Badge>
                          </td>
                          <td className="p-4 text-sm">{s.full_name || '-'}</td>
                          <td className="p-4 text-sm">{s.email}</td>
                          <td className="p-4 text-sm max-w-xs truncate">
                            {s.type === 'join_drop' ? `Drop: ${s.drop_id?.substring(0, 10)}...` : `Brand: ${s.brand}`}
                          </td>
                          <td className="p-4 text-sm max-w-xs truncate">{s.notes || '-'}</td>
                          <td className="p-4 text-sm text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</td>
                          <td className="p-4">
                            {s.handled ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                              <XCircle className="w-5 h-5 text-gray-300" />
                            )}
                          </td>
                          <td className="p-4">
                            <Button size="sm" variant={s.handled ? "outline" : "default"} onClick={() => handleMarkSubmissionHandled(s.id, !s.handled)}>
                              {s.handled ? 'Unmark' : 'Mark Handled'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold">Pending Payments</h2>
                <Button variant="outline" onClick={fetchPendingPayments} className="gap-2">
                  <RefreshCw className="w-4 h-4" /> Refresh
                </Button>
              </div>

              {pendingPayments.length === 0 ? (
                <Card className="border-stone-200">
                  <CardContent className="p-12 text-center">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <p className="text-lg text-muted-foreground">No pending payments to verify</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {pendingPayments.map((payment) => (
                    <Card key={payment.id} className="border-stone-200">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">{payment.user_name || payment.user_email}</p>
                            <p className="text-sm text-muted-foreground">{payment.user_email}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              Method: {payment.payment_method} | Reference: {payment.reference || 'N/A'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold">SGD {payment.amount?.toFixed(2)}</p>
                            <p className="text-sm text-muted-foreground">{new Date(payment.submitted_at).toLocaleString()}</p>
                            <Button size="sm" className="mt-2 bg-green-600 hover:bg-green-700" onClick={() => handleVerifyPayment(payment.id)}>
                              <CheckCircle className="w-4 h-4 mr-1" /> Verify
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Email Logs Tab */}
          {activeTab === 'emails' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h2 className="text-2xl font-semibold">Email Logs</h2>
                <div className="flex gap-2">
                  <Select value={emailStatusFilter} onValueChange={setEmailStatusFilter}>
                    <SelectTrigger className="w-[140px]">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="success">Success</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={fetchEmailLogs} className="gap-2">
                    <RefreshCw className="w-4 h-4" /> Refresh
                  </Button>
                </div>
              </div>

              <Card className="border-stone-200">
                <CardContent className="p-0 max-h-[600px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-200 sticky top-0">
                      <tr>
                        <th className="text-left p-4 font-medium text-sm">Recipient</th>
                        <th className="text-left p-4 font-medium text-sm">Subject</th>
                        <th className="text-left p-4 font-medium text-sm">Status</th>
                        <th className="text-left p-4 font-medium text-sm">Sent At</th>
                        <th className="text-left p-4 font-medium text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailLogs.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="p-8 text-center text-muted-foreground">No email logs found</td>
                        </tr>
                      ) : (
                        emailLogs.map((log) => (
                          <tr key={log.id} className="border-b border-stone-100 last:border-0">
                            <td className="p-4 text-sm">{log.recipient}</td>
                            <td className="p-4 text-sm truncate max-w-xs">{log.subject}</td>
                            <td className="p-4">
                              <Badge className={log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                {log.status}
                              </Badge>
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">{new Date(log.sent_at || log.attempted_at).toLocaleString()}</td>
                            <td className="p-4">
                              {log.status === 'failed' && (
                                <Button size="sm" variant="outline" onClick={() => handleResendEmail(log.id)}>
                                  <Send className="w-4 h-4 mr-1" /> Resend
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Content Tab */}
          {activeTab === 'content' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold">Content Management</h2>
                <Button variant="outline" onClick={fetchContentBlocks} className="gap-2">
                  <RefreshCw className="w-4 h-4" /> Refresh
                </Button>
              </div>

              <div className="grid gap-4">
                {contentBlocks.map((block) => (
                  <Card key={block.id} className="border-stone-200">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">{block.title}</CardTitle>
                          <CardDescription>Type: {block.type.replace(/_/g, ' ')}</CardDescription>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => openEditContentDialog(block)}>
                          <Edit className="w-4 h-4 mr-1" /> Edit
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground whitespace-pre-line">{block.body}</p>
                      <p className="text-xs text-muted-foreground mt-4">
                        Last updated: {new Date(block.updated_at).toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Legal Tab */}
          {activeTab === 'legal' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold">Legal Documents</h2>
                <Button variant="outline" onClick={fetchLegalDocuments} className="gap-2">
                  <RefreshCw className="w-4 h-4" /> Refresh
                </Button>
              </div>

              <Card className="border-stone-200 bg-primary/5">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <Shield className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-primary mb-1">Version Control</h4>
                      <p className="text-sm text-muted-foreground">
                        When you <strong>publish</strong> changes, the version number increments and all users will be required to re-accept the updated terms on their next login.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid gap-4">
                {legalDocuments.length === 0 ? (
                  <Card className="border-stone-200">
                    <CardContent className="p-8 text-center">
                      <p className="text-muted-foreground">No legal documents found. Default terms will be displayed.</p>
                    </CardContent>
                  </Card>
                ) : (
                  legalDocuments.map((doc) => (
                    <Card key={doc.id} className="border-stone-200">
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start">
                          <div className="flex items-start gap-3">
                            {doc.type === 'terms_and_conditions' ? (
                              <FileText className="w-6 h-6 text-primary mt-1" />
                            ) : (
                              <Shield className="w-6 h-6 text-primary mt-1" />
                            )}
                            <div>
                              <CardTitle className="text-lg">{doc.title}</CardTitle>
                              <CardDescription>
                                Type: {doc.type.replace(/_/g, ' ')} | Version: <strong>{doc.version}</strong>
                                {doc.is_active && <Badge className="ml-2 bg-green-100 text-green-800">Active</Badge>}
                              </CardDescription>
                            </div>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => openEditLegalDialog(doc)}>
                            <Edit className="w-4 h-4 mr-1" /> Edit
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                          <p className="text-sm text-muted-foreground whitespace-pre-line font-mono">
                            {doc.content?.substring(0, 500)}...
                          </p>
                        </div>
                        <div className="flex justify-between items-center mt-4 text-xs text-muted-foreground">
                          <span>Last updated: {new Date(doc.updated_at).toLocaleString()}</span>
                          <div className="flex gap-2">
                            <Link to={doc.type === 'terms_and_conditions' ? '/terms-and-conditions' : '/privacy-policy'} target="_blank">
                              <Button size="sm" variant="ghost" className="text-primary">
                                <Eye className="w-4 h-4 mr-1" /> Preview
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Upload Invoice</h2>
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle>Invoice Details</CardTitle>
                  <CardDescription>Upload actual shipping costs for allocation</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <Label>Select Drop</Label>
                    <Select value={selectedDrop?.id || ''} onValueChange={(value) => { const drop = allDrops.find(d => d.id === value); setSelectedDrop(drop); }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a drop" />
                      </SelectTrigger>
                      <SelectContent>
                        {allDrops.filter(d => ['ordered', 'in_transit', 'ready_for_collection'].includes(d.status)).map(drop => (
                          <SelectItem key={drop.id} value={drop.id}>{drop.name} ({drop.status})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedDrop && (
                    <form onSubmit={handleUploadInvoice} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="shipping_standard">Standard Shipping (SGD)</Label>
                          <Input id="shipping_standard" type="number" step="0.01" value={invoiceForm.shipping_standard} onChange={(e) => setInvoiceForm({ ...invoiceForm, shipping_standard: Number(e.target.value) })} required />
                        </div>
                        <div>
                          <Label htmlFor="shipping_oversize">Oversize Shipping (SGD)</Label>
                          <Input id="shipping_oversize" type="number" step="0.01" value={invoiceForm.shipping_oversize} onChange={(e) => setInvoiceForm({ ...invoiceForm, shipping_oversize: Number(e.target.value) })} required />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="duties_taxes">Duties & Taxes (SGD)</Label>
                        <Input id="duties_taxes" type="number" step="0.01" value={invoiceForm.duties_taxes} onChange={(e) => setInvoiceForm({ ...invoiceForm, duties_taxes: Number(e.target.value) })} />
                      </div>
                      <div>
                        <Label htmlFor="notes">Notes</Label>
                        <Input id="notes" value={invoiceForm.notes} onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })} />
                      </div>
                      <Button type="submit" className="w-full bg-primary hover:bg-primary/90">Upload Invoice</Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Allocations Tab */}
          {activeTab === 'allocations' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Run Allocation Engine</h2>
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle>Select Drop for Allocation</CardTitle>
                  <CardDescription>This will calculate and distribute shipping costs fairly among participants</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {allDrops.filter(d => ['ordered', 'in_transit', 'ready_for_collection'].includes(d.status)).length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No drops ready for allocation. Drops must be in Ordered status or later.</p>
                  ) : (
                    allDrops.filter(d => ['ordered', 'in_transit', 'ready_for_collection'].includes(d.status)).map(drop => (
                      <div key={drop.id} className="flex justify-between items-center p-4 border border-stone-200 rounded-lg">
                        <div>
                          <h3 className="font-semibold">{drop.name}</h3>
                          <p className="text-sm text-muted-foreground">{drop.participant_count} participants • {drop.status}</p>
                        </div>
                        <Button onClick={() => handleRunAllocation(drop.id)} className="bg-primary hover:bg-primary/90">
                          Run Allocation
                        </Button>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Audit Logs Tab */}
          {activeTab === 'audit' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold">Audit Logs</h2>
                <Button variant="outline" onClick={fetchAuditLogs} className="gap-2">
                  <RefreshCw className="w-4 h-4" /> Refresh
                </Button>
              </div>

              <Card className="border-stone-200">
                <CardContent className="p-0 max-h-[600px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-200 sticky top-0">
                      <tr>
                        <th className="text-left p-4 font-medium text-sm">Action</th>
                        <th className="text-left p-4 font-medium text-sm">User</th>
                        <th className="text-left p-4 font-medium text-sm">Details</th>
                        <th className="text-left p-4 font-medium text-sm">Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="p-8 text-center text-muted-foreground">No audit logs yet</td>
                        </tr>
                      ) : (
                        auditLogs.map((log) => (
                          <tr key={log.id} className="border-b border-stone-100 last:border-0">
                            <td className="p-4">
                              <Badge variant="outline">{log.action}</Badge>
                            </td>
                            <td className="p-4 text-sm">{log.user_id || log.user_email || 'System'}</td>
                            <td className="p-4 text-sm text-muted-foreground truncate max-w-xs">
                              {log.drop_id && `Drop: ${log.drop_id.substring(0, 8)}...`}
                              {log.old_status && ` ${log.old_status} → ${log.new_status}`}
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">{new Date(log.timestamp).toLocaleString()}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>
      
      {/* Test Email Dialog */}
      <Dialog open={showTestEmailDialog} onOpenChange={setShowTestEmailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Test Broadcast Email</DialogTitle>
            <DialogDescription>Send a test notification email before broadcasting to all users</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="test-email">Test Email Address</Label>
              <Input id="test-email" type="email" placeholder="your@email.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="mt-2" />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => handlePublishDrop(dropToPublish?.id, testEmail)} className="flex-1 bg-primary hover:bg-primary/90" disabled={!testEmail}>Send Test</Button>
              <Button onClick={() => { setShowTestEmailDialog(false); setTestEmail(''); }} variant="outline" className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Drop Dialog */}
      <Dialog open={showEditDropDialog} onOpenChange={setShowEditDropDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Drop: {selectedDrop?.brand}</DialogTitle>
            <DialogDescription>Update drop details and thresholds</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditDrop} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input value={editDropForm.name || ''} onChange={(e) => setEditDropForm({ ...editDropForm, name: e.target.value })} />
              </div>
              <div>
                <Label>Brand</Label>
                <Input value={editDropForm.brand || ''} onChange={(e) => setEditDropForm({ ...editDropForm, brand: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={editDropForm.description || ''} onChange={(e) => setEditDropForm({ ...editDropForm, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Min Participants</Label>
                <Input type="number" value={editDropForm.min_participants || 3} onChange={(e) => setEditDropForm({ ...editDropForm, min_participants: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Deposit %</Label>
                <Input type="number" value={editDropForm.deposit_percent || 50} onChange={(e) => setEditDropForm({ ...editDropForm, deposit_percent: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={editDropForm.location || ''} onChange={(e) => setEditDropForm({ ...editDropForm, location: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Service Fee (Standard) SGD</Label>
                <Input type="number" step="0.01" value={editDropForm.service_fee_standard || 8} onChange={(e) => setEditDropForm({ ...editDropForm, service_fee_standard: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Service Fee (Oversize) SGD</Label>
                <Input type="number" step="0.01" value={editDropForm.service_fee_oversize || 12} onChange={(e) => setEditDropForm({ ...editDropForm, service_fee_oversize: Number(e.target.value) })} />
              </div>
            </div>
            <div>
              <Label>Collection Info</Label>
              <Input value={editDropForm.collection_info || ''} onChange={(e) => setEditDropForm({ ...editDropForm, collection_info: e.target.value })} placeholder="e.g., Central location TBC" />
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90">Save Changes</Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowEditDropDialog(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Content Dialog */}
      <Dialog open={showEditContentDialog} onOpenChange={setShowEditContentDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Content: {selectedContent?.type?.replace(/_/g, ' ')}</DialogTitle>
            <DialogDescription>Update this content block. Changes will reflect immediately.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateContent} className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={editContentForm.title || ''} onChange={(e) => setEditContentForm({ ...editContentForm, title: e.target.value })} />
            </div>
            <div>
              <Label>Body</Label>
              <Textarea value={editContentForm.body || ''} onChange={(e) => setEditContentForm({ ...editContentForm, body: e.target.value })} rows={6} />
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90">Save Changes</Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowEditContentDialog(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Management Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage User</DialogTitle>
            <DialogDescription>{selectedUser?.email}</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="p-4 bg-stone-50 rounded-lg space-y-2">
                <p><strong>Name:</strong> {selectedUser.full_name || '-'}</p>
                <p><strong>Phone:</strong> {selectedUser.phone || '-'}</p>
                <p><strong>Joined:</strong> {new Date(selectedUser.created_at).toLocaleDateString()}</p>
                <p><strong>Drops Joined:</strong> {selectedUser.drops_joined || 0}</p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Admin Access</Label>
                  <Switch 
                    checked={selectedUser.is_admin || false} 
                    onCheckedChange={(checked) => handleUpdateUser(selectedUser.id, { is_admin: checked })} 
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Drop Alerts</Label>
                  <Switch 
                    checked={selectedUser.drop_alerts_opt_in !== false} 
                    onCheckedChange={(checked) => handleUpdateUser(selectedUser.id, { drop_alerts_opt_in: checked })} 
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Account Status</Label>
                  <Select 
                    value={selectedUser.status || 'active'} 
                    onValueChange={(value) => handleUpdateUser(selectedUser.id, { status: value })}
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button variant="outline" className="w-full gap-2" onClick={() => handleTriggerPasswordReset(selectedUser.id)}>
                  <Key className="w-4 h-4" /> Send Password Reset Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Submissions/Participants Viewer Dialog */}
      <Dialog open={showSubmissionsDialog} onOpenChange={setShowSubmissionsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Participants for {selectedDrop?.name}</DialogTitle>
            <DialogDescription>{submissions.length} participants have joined this drop</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {submissions.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No participants yet</p>
            ) : (
              <div className="space-y-4">
                {submissions.map((participant) => (
                  <Card key={participant.id} className="border-stone-200">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">{participant.full_name || 'Unknown User'}</CardTitle>
                          <CardDescription>{participant.email} {participant.phone && `• ${participant.phone}`}</CardDescription>
                        </div>
                        <Badge variant={participant.submission?.status === 'submitted' ? 'default' : 'secondary'}>
                          {participant.submission?.status || 'joined'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {participant.submission?.items && (
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">Items:</p>
                            <ul className="text-sm space-y-1">
                              {participant.submission.items.map((item, i) => (
                                <li key={i} className="flex justify-between">
                                  <span>{item.category} - {item.name}</span>
                                  <span>Qty: {item.quantity}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {participant.submission?.solo_estimate > 0 && (
                          <p className="text-sm"><span className="text-muted-foreground">Solo Estimate:</span> SGD {participant.submission.solo_estimate}</p>
                        )}
                        {participant.submission?.notes && (
                          <p className="text-sm"><span className="text-muted-foreground">Notes:</span> {participant.submission.notes}</p>
                        )}
                        <p className="text-xs text-muted-foreground">Joined: {new Date(participant.joined_at).toLocaleString()}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Legal Document Dialog */}
      <Dialog open={showEditLegalDialog} onOpenChange={setShowEditLegalDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Legal Document</DialogTitle>
            <DialogDescription>
              {selectedLegal?.type === 'terms_and_conditions' ? 'Terms & Conditions' : 'Privacy Policy'} - Current Version: {selectedLegal?.version}
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input 
                value={editLegalForm.title || ''} 
                onChange={(e) => setEditLegalForm({ ...editLegalForm, title: e.target.value })} 
              />
            </div>
            <div>
              <Label>Content (Markdown supported)</Label>
              <Textarea 
                value={editLegalForm.content || ''} 
                onChange={(e) => setEditLegalForm({ ...editLegalForm, content: e.target.value })} 
                rows={20}
                className="font-mono text-sm"
              />
            </div>
            <div className="flex gap-2 pt-4 border-t">
              <Button 
                type="button" 
                onClick={(e) => handleUpdateLegalDocument(e, false)} 
                variant="outline" 
                className="flex-1"
              >
                Save Draft
              </Button>
              <Button 
                type="button" 
                onClick={(e) => handleUpdateLegalDocument(e, true)} 
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                Publish & Increment Version
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Publishing will increment the version number and require all users to re-accept the updated terms.
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Withdraw User Dialog */}
      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Withdraw User from Drop</DialogTitle>
            <DialogDescription>Remove a user from a specific drop</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Drop</Label>
              <Select value={withdrawDropId} onValueChange={setWithdrawDropId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a drop" />
                </SelectTrigger>
                <SelectContent>
                  {allDrops.map(drop => (
                    <SelectItem key={drop.id} value={drop.id}>{drop.name} ({drop.brand}) - {drop.participant_count} participants</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>User Email</Label>
              <Input 
                type="email" 
                placeholder="user@example.com" 
                value={withdrawEmail} 
                onChange={(e) => setWithdrawEmail(e.target.value)} 
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleWithdrawUser} className="flex-1 bg-red-600 hover:bg-red-700">
                <UserMinus className="w-4 h-4 mr-2" /> Withdraw User
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setShowWithdrawDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Order Dialog */}
      <Dialog open={showUserOrderDialog} onOpenChange={setShowUserOrderDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedUserOrder ? 'Edit User Order' : 'Add User Order'}</DialogTitle>
            <DialogDescription>Record items purchased, shipping, and service fees for a user</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUserOrder} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>User Email</Label>
                <Input 
                  type="email" 
                  placeholder="user@example.com" 
                  value={userOrderForm.user_email}
                  onChange={(e) => setUserOrderForm({ ...userOrderForm, user_email: e.target.value })}
                  required
                  disabled={selectedUserOrder !== null}
                />
              </div>
              <div>
                <Label>Drop</Label>
                <Select 
                  value={userOrderForm.drop_id} 
                  onValueChange={(value) => setUserOrderForm({ ...userOrderForm, drop_id: value })}
                  disabled={selectedUserOrder !== null}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select drop" />
                  </SelectTrigger>
                  <SelectContent>
                    {allDrops.map(drop => (
                      <SelectItem key={drop.id} value={drop.id}>{drop.name} ({drop.brand})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Items */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Items</Label>
                <Button type="button" size="sm" variant="outline" onClick={addItemToOrder}>
                  + Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {userOrderForm.items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start p-3 bg-stone-50 rounded-lg">
                    <div className="flex-1 grid grid-cols-4 gap-2">
                      <Input 
                        placeholder="Item name" 
                        value={item.name}
                        onChange={(e) => updateOrderItem(index, 'name', e.target.value)}
                      />
                      <Select value={item.category} onValueChange={(v) => updateOrderItem(index, 'category', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mallet">Mallet</SelectItem>
                          <SelectItem value="boots">Boots</SelectItem>
                          <SelectItem value="helmet">Helmet</SelectItem>
                          <SelectItem value="gloves">Gloves</SelectItem>
                          <SelectItem value="kneepads">Knee Pads</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input 
                        type="number" 
                        placeholder="Qty" 
                        value={item.quantity}
                        onChange={(e) => updateOrderItem(index, 'quantity', Number(e.target.value))}
                        min="1"
                      />
                      <Input 
                        type="number" 
                        step="0.01"
                        placeholder="Price" 
                        value={item.unit_price}
                        onChange={(e) => updateOrderItem(index, 'unit_price', Number(e.target.value))}
                      />
                    </div>
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeItemFromOrder(index)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Cost Breakdown */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Item Total (SGD)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={userOrderForm.item_total}
                  onChange={(e) => setUserOrderForm({ ...userOrderForm, item_total: Number(e.target.value) })}
                />
              </div>
              <div>
                <Label>Shipping Fee (SGD)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={userOrderForm.shipping_fee}
                  onChange={(e) => setUserOrderForm({ ...userOrderForm, shipping_fee: Number(e.target.value) })}
                />
              </div>
              <div>
                <Label>Service Fee (SGD)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={userOrderForm.service_fee}
                  onChange={(e) => setUserOrderForm({ ...userOrderForm, service_fee: Number(e.target.value) })}
                />
              </div>
            </div>

            <div className="bg-stone-100 p-3 rounded-lg">
              <div className="flex justify-between font-semibold">
                <span>Grand Total:</span>
                <span>SGD {(userOrderForm.item_total + userOrderForm.shipping_fee + userOrderForm.service_fee).toFixed(2)}</span>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea 
                value={userOrderForm.notes}
                onChange={(e) => setUserOrderForm({ ...userOrderForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90">
                {selectedUserOrder ? 'Update Order' : 'Save Order'}
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowUserOrderDialog(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
